import { loadStripe } from '@stripe/stripe-js';
import config from '../../config';

let stripePromise;

// Initialize Stripe
const getStripe = async () => {
  if (!stripePromise) {
    // Use the publishable key from config (set at build time)
    const publishableKey = config.stripePublicKey;
    if (!publishableKey) {
      console.error('Stripe publishable key is not set');
      throw new Error('Stripe configuration error');
    }
    console.log('Initializing Stripe with key:', publishableKey.substring(0, 8) + '...');
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

// Redirect to Stripe Checkout
export const redirectToCheckout = async (token) => {
  try {
    const response = await fetch(`/api/stripe/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    
    const { url } = await response.json();
    
    // Normalize and clean the URL
    let cleanUrl = url.replace('/react/react/', '/react/').replace('//stripe-success', '/stripe-success');

    // Remove any GitHub codespaces domain
    if (cleanUrl.includes('github.dev')) {
      // Extract just the path portion
      const urlPath = new URL(cleanUrl).pathname;
      cleanUrl = urlPath;
    }

    // Use our normalize function
    const normalizedUrl = config.normalizeUrl(cleanUrl);
    console.log(`Redirecting to Stripe: ${normalizedUrl}`);
    window.location.href = normalizedUrl;
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    return { error };
  }
};

// Redirect to Stripe Customer Portal
export const redirectToCustomerPortal = async (token) => {
  try {
    console.log('Creating customer portal session...');
    
    // For debugging - show only the first few characters of the token
    const tokenPreview = token ? `${token.substring(0, 10)}...` : 'undefined';
    console.log(`Using auth token: ${tokenPreview}`);
    
    const response = await fetch(`/api/stripe/create-customer-portal-session`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      // Try to get more detailed error information
      let errorMessage = 'Network response was not ok';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
        console.error('Server error details:', errorData);
      } catch (e) {
        // If we can't parse JSON, try to get text
        try {
          errorMessage = await response.text() || errorMessage;
        } catch (textError) {
          console.error('Could not read error response as text:', textError);
        }
      }
      
      console.error(`Failed to create portal session: ${response.status} - ${errorMessage}`);
      throw new Error(errorMessage);
    }
    
    const responseData = await response.json();
    console.log('Portal session created successfully');
    
    if (!responseData.url) {
      console.error('Missing URL in portal session response:', responseData);
      throw new Error('Invalid response from server: missing URL');
    }
    
    const { url } = responseData;
    console.log(`Original portal URL: ${url}`);
    
    // Normalize and clean the URL
    let cleanUrl = url.replace('/react/react/', '/react/');

    // Remove any GitHub codespaces domain
    if (cleanUrl.includes('github.dev')) {
      // Extract just the path portion
      const urlPath = new URL(cleanUrl).pathname;
      cleanUrl = urlPath;
    }

    // Use our normalize function
    const normalizedUrl = config.normalizeUrl(cleanUrl);
    console.log(`Redirecting to Customer Portal: ${normalizedUrl}`);
    
    // Add a small delay to ensure logging completes
    setTimeout(() => {
      window.location.href = normalizedUrl;
    }, 100);
    
    return { success: true };
  } catch (error) {
    console.error('Error redirecting to customer portal:', error);
    alert(`Could not access subscription management portal. Please try again later or contact support.\n\nError: ${error.message}`);
    return { error };
  }
};